import { Component, HostBinding, Input } from '@angular/core';

type AstralLayoutSpan = number | 'full';
type AstralLayoutAlignItems = 'start' | 'center' | 'end' | 'stretch';

@Component({
  selector: 'astral-layout-group',
  standalone: true,
  template: `
    <div
      class="astral-layout-group"
    >
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['./astral-layout-group.component.scss'],
})
export class AstralLayoutGroupComponent {
  @Input() columns = 1;
  @Input() tabletColumns: number;
  @Input() mobileColumns: number;
  @Input() alignItems: AstralLayoutAlignItems = 'start';

  @Input() span: AstralLayoutSpan = 1;
  @Input() tabletSpan: AstralLayoutSpan;
  @Input() mobileSpan: AstralLayoutSpan;

  @Input() minColumnWidth = '220px';
  @Input() rowGap = 'var(--astral-form-row-gap, var(--spacing-xxs, 6px))';
  @Input() columnGap = 'var(--astral-form-column-gap, var(--spacing-xl, 24px))';
  // Toggle-only grids keep the compact row rhythm and do not reserve field-error space.
  @Input() denseBooleanControls = false;
  // Use only for a guaranteed single field row inside an Astral surface.
  @Input() useSurfaceErrorPadding = false;

  @HostBinding('style.--astral-layout-group-grid-column')
  get resolvedGridColumn(): string | null {
    return this.formatSpan(this.span);
  }

  @HostBinding('style.--astral-layout-group-tablet-grid-column')
  get resolvedTabletGridColumn(): string | null {
    if (this.tabletSpan !== undefined && this.tabletSpan !== null) {
      return this.formatSpan(this.tabletSpan);
    }

    return this.formatResponsiveSpan(this.span, 2);
  }

  @HostBinding('style.--astral-layout-group-mobile-grid-column')
  get resolvedMobileGridColumn(): string | null {
    if (this.mobileSpan !== undefined && this.mobileSpan !== null) {
      return this.formatSpan(this.mobileSpan);
    }

    return this.formatResponsiveSpan(this.span, 1);
  }

  @HostBinding('style.--astral-layout-group-align-items')
  get resolvedAlignItems(): AstralLayoutAlignItems {
    return this.alignItems ?? 'start';
  }

  // Keep layout variables on the host so parent contexts can override them responsively.
  @HostBinding('style.--astral-layout-group-grid-template-columns')
  get hostGridTemplateColumns(): string {
    return this.resolvedGridTemplateColumns;
  }

  @HostBinding('style.--astral-layout-group-tablet-grid-template-columns')
  get hostTabletGridTemplateColumns(): string | null {
    return this.resolvedTabletGridTemplateColumns;
  }

  @HostBinding('style.--astral-layout-group-mobile-grid-template-columns')
  get hostMobileGridTemplateColumns(): string | null {
    return this.resolvedMobileGridTemplateColumns;
  }

  @HostBinding('style.--astral-layout-group-column-gap')
  get hostColumnGap(): string {
    return this.columnGap;
  }

  @HostBinding('style.--astral-layout-group-row-gap')
  get hostRowGap(): string {
    return this.rowGap;
  }

  @HostBinding('class.astral-layout-group--dense-boolean-controls')
  get hasDenseBooleanControls(): boolean {
    return this.denseBooleanControls;
  }

  @HostBinding('class.astral-layout-group--surface-error-padding')
  get usesSurfaceErrorPadding(): boolean {
    return this.useSurfaceErrorPadding;
  }

  get resolvedGridTemplateColumns(): string {
    if (!this.columns || this.columns < 1) {
      return `repeat(auto-fit, minmax(${this.minColumnWidth}, 1fr))`;
    }

    return `repeat(${this.columns}, minmax(0, 1fr))`;
  }

  get resolvedTabletGridTemplateColumns(): string | null {
    if (this.tabletColumns && this.tabletColumns > 0) {
      return `repeat(${this.tabletColumns}, minmax(0, 1fr))`;
    }

    if (!this.columns || this.columns < 1) {
      return null;
    }

    return `repeat(${Math.min(this.columns, 2)}, minmax(0, 1fr))`;
  }

  get resolvedMobileGridTemplateColumns(): string | null {
    if (this.mobileColumns && this.mobileColumns > 0) {
      return `repeat(${this.mobileColumns}, minmax(0, 1fr))`;
    }

    if (!this.columns || this.columns < 1) {
      return null;
    }

    return `repeat(${Math.min(this.columns, 1)}, minmax(0, 1fr))`;
  }

  private formatResponsiveSpan(span: AstralLayoutSpan, maxColumns: number): string | null {
    if (span === 'full') {
      return '1 / -1';
    }

    if (!span || span <= 1) {
      return null;
    }

    const resolvedSpan = Math.min(span, maxColumns);
    return resolvedSpan <= 1 ? null : `span ${resolvedSpan}`;
  }

  private formatSpan(span: AstralLayoutSpan): string | null {
    if (span === 'full') {
      return '1 / -1';
    }

    if (!span || span <= 1) {
      return null;
    }

    return `span ${span}`;
  }
}
